import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { HiOutlineBookOpen } from 'react-icons/hi2';
import Button from 'components/Common/Button';
import { BusinessAddon } from '@/apis/business/addons/types';
import {
  AddonPriceBadgePiece,
  formatDisplayDate,
  getAddonDisplayTitle,
  getAddonIcon,
  getAddonPriceBadgePieces,
  getAddonShortBlurb,
  isAddonActive,
  isAddonPendingCancellation
} from './addonDisplay';

interface AddonCardProps {
  addon: BusinessAddon;
  onActivate: () => void;
  onTerms: () => void;
  onDeactivate: () => void;
  onKeepActive?: () => void;
  onManage?: () => void;
}

function AddonPriceBadgeLabel({ pieces }: { pieces: AddonPriceBadgePiece[] }) {
  const theme = useTheme();
  const amountColor = theme.palette.primary.dark;
  const unitColor = theme.palette.primary.main;

  return (
    <Box component='span' sx={{ display: 'inline', fontSize: 12, lineHeight: 1.4, color: unitColor }}>
      {pieces.map((piece, index) => {
        if (piece.type === 'dot') {
          return (
            <Box component='span' key={`dot-${index}`} sx={{ fontWeight: 400 }}>
              {' · '}
            </Box>
          );
        }

        if (piece.type === 'text') {
          return (
            <Box component='span' key={`text-${index}`} sx={{ fontWeight: 400 }}>
              {piece.value}
            </Box>
          );
        }

        const emphasize = piece.emphasizeAmount !== false;

        return (
          <Box component='span' key={`seg-${index}`}>
            <Box
              component='span'
              sx={{
                color: emphasize ? amountColor : unitColor,
                fontWeight: emphasize ? 700 : 400
              }}
            >
              {piece.amount}
            </Box>
            <Box component='span' sx={{ color: unitColor, fontWeight: 400 }}>
              {piece.unit}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function StatusPill({ label, tone }: { label: string; tone: 'active' | 'pending' | 'inactive' }) {
  const theme = useTheme();

  const styles =
    tone === 'pending'
      ? { color: theme.palette.warning.main, bgcolor: theme.palette.warning.light }
      : tone === 'active'
        ? { color: theme.palette.success.main, bgcolor: theme.palette.success.light }
        : { color: theme.palette.text.secondary, bgcolor: '#F3F4F6' };

  return (
    <Typography
      component='span'
      noWrap
      sx={{
        ...styles,
        borderRadius: '100px',
        px: 1.25,
        py: 0.25,
        fontSize: 12,
        fontWeight: 500,
        width: 'fit-content',
        display: 'inline-block',
        mt: 0.5
      }}
    >
      {label}
    </Typography>
  );
}

export default function AddonCard({
  addon,
  onActivate,
  onTerms,
  onDeactivate,
  onKeepActive,
  onManage
}: AddonCardProps) {
  const theme = useTheme();
  const active = isAddonActive(addon);
  const pendingCancel = isAddonPendingCancellation(addon);
  const canActivate = addon.purchasable && !active;
  const pricePieces = getAddonPriceBadgePieces(addon);

  const statusTone: 'active' | 'pending' | 'inactive' = pendingCancel ? 'pending' : active ? 'active' : 'inactive';
  const statusLabel = pendingCancel
    ? `Ends ${formatDisplayDate(addon.subscription?.cancel_at)}`
    : active
      ? 'Active'
      : 'Inactive';

  return (
    <Box
      bgcolor='#fff'
      border='1px solid #ECECEC'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      height='100%'
      minHeight={220}
    >
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap={1.5}>
        <Stack direction='row' alignItems='flex-start' gap={1.5} minWidth={0}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {getAddonIcon(addon.code)}
          </Box>
          <Box minWidth={0}>
            <Typography fontWeight={600} fontSize={16}>
              {getAddonDisplayTitle(addon)}
            </Typography>
            <StatusPill label={statusLabel} tone={statusTone} />
          </Box>
        </Stack>

        <Chip
          label={<AddonPriceBadgeLabel pieces={pricePieces} />}
          size='small'
          sx={{
            bgcolor: theme.palette.primary.light,
            borderRadius: '100px',
            height: 'auto',
            py: 0.75,
            maxWidth: '55%',
            flexShrink: 0,
            '& .MuiChip-label': {
              whiteSpace: 'normal',
              px: 1.5,
              py: 0.25,
              textAlign: 'left'
            }
          }}
        />
      </Stack>

      <Typography mt={1.5} color='text.secondary' fontSize={13} lineHeight={1.5} sx={{ flex: 1 }}>
        {getAddonShortBlurb(addon)}
      </Typography>

      <Stack direction='row' alignItems='center' justifyContent='space-between' mt={2} gap={1} flexWrap='wrap'>
        <Box
          component='button'
          type='button'
          onClick={onTerms}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            color: theme.palette.primary.main,
            fontSize: 13,
            fontWeight: 500,
            p: 0,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <HiOutlineBookOpen size={16} />
          Terms & Offerings
        </Box>

        {canActivate ? (
          <Button variant='contained' onClick={onActivate} sx={{ textTransform: 'none', px: 2.5 }}>
            Activate
          </Button>
        ) : pendingCancel ? (
          <Stack direction='row' gap={1} flexWrap='wrap'>
            {onManage && (
              <Button variant='outlined' onClick={onManage} sx={{ textTransform: 'none', px: 2 }}>
                Manage
              </Button>
            )}
            <Button variant='contained' color='warning' onClick={onKeepActive} sx={{ textTransform: 'none', px: 2 }}>
              Keep Add-On Active
            </Button>
          </Stack>
        ) : active ? (
          <Stack direction='row' gap={1} flexWrap='wrap'>
            {onManage && (
              <Button variant='outlined' onClick={onManage} sx={{ textTransform: 'none', px: 2 }}>
                Manage
              </Button>
            )}
            <Button variant='contained' color='error' onClick={onDeactivate} sx={{ textTransform: 'none', px: 2 }}>
              Deactivate Add-On
            </Button>
          </Stack>
        ) : (
          <Button variant='contained' disabled sx={{ textTransform: 'none', px: 2.5 }}>
            Unavailable
          </Button>
        )}
      </Stack>
    </Box>
  );
}
